import React, { useCallback } from 'react'

import { Button, T, TextArea } from 'src/govwell-ui'
import { KeyboardKey } from 'src/types'

import PromptButton from './PromptButton'

type Props = {
  value: string
  onValueChange: (value: string) => void
  onSend: () => void
  isDisabled: boolean
  suggestedPrompts?: string[]
  onSelectPrompt?: (prompt: string) => void
  showPrompts?: boolean
  disclaimer?: string
}

const AIChatInputArea = ({
  value,
  onValueChange,
  onSend,
  isDisabled,
  suggestedPrompts = [],
  onSelectPrompt,
  showPrompts = false,
  disclaimer = 'AI can make mistakes. Verify important information.',
}: Props) => {
  const isSendDisabled = isDisabled || !value.trim()

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        e.key === KeyboardKey.Enter &&
        !e.shiftKey &&
        !e.nativeEvent.isComposing &&
        value.trim()
      ) {
        e.preventDefault()
        onSend()
      }
    },
    [onSend, value]
  )

  return (
    <div className="shrink-0 border-t border-neutral-200 bg-white py-4">
      <div className="mx-auto w-full max-w-4xl px-6">
        <div className="mb-3 flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm [&_textarea]:!border-0 [&_textarea]:focus-within:![box-shadow:none] [&_textarea]:disabled:!bg-white">
          <TextArea
            ariaLabel="Message to send to AI Assistant"
            placeholder="Ask anything..."
            width="100%"
            value={value}
            minRows={1}
            maxRows={4}
            onValueChange={onValueChange}
            onKeyDown={handleKeyDown}
            isDisabled={isDisabled}
          />
          <Button
            variant="primary"
            onClick={onSend}
            isDisabled={isSendDisabled}
            ariaLabel="Send message"
            icon="FaPaperPlane"
            size="md"
          />
        </div>

        {showPrompts && suggestedPrompts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {suggestedPrompts.map((prompt) => (
              <PromptButton key={prompt} prompt={prompt} onSelect={onSelectPrompt} />
            ))}
          </div>
        )}

        <T.BodySmall className="mt-3 text-center text-neutral-500">{disclaimer}</T.BodySmall>
      </div>
    </div>
  )
}

export default React.memo(AIChatInputArea)
